import React, { useEffect, useState } from "react";
import "./Profile.css";
import { showToast } from "../../utils/customToast";
import { Link } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import { ProfileSkeleton } from "../Skeleton";

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔐 Password states
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [changing, setChanging] = useState(false);

  const { selectedCompany } = useCompany();
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

  // =============================
  // Fetch profile
  // =============================
  useEffect(() => {
    const userId = localStorage.getItem("userId");

    if (!userId) {
      setLoading(false);
      return;
    }

    fetch(`${BASE_API_URL}/profile/${userId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setProfile(data.data);
        } else {
          // Fallback to local storage if API profile not found
          setProfile({
            name: localStorage.getItem("username") || "User",
            email: localStorage.getItem("email") || "user@example.com",
            mobile: localStorage.getItem("mobile") || "N/A",
            address: localStorage.getItem("address") || "N/A",
            active: true,
            maxCompanies: 5,
            accountCreatedOn: new Date().toISOString(),
          });
        }
        setLoading(false);
      })
      .catch(() => {
        // Graceful fallback
        setProfile({
          name: localStorage.getItem("username") || "User",
          email: localStorage.getItem("email") || "user@example.com",
          mobile: localStorage.getItem("mobile") || "N/A",
          address: localStorage.getItem("address") || "N/A",
          active: true,
          maxCompanies: 5,
          accountCreatedOn: new Date().toISOString(),
        });
        setLoading(false);
      });
  }, [BASE_API_URL]);

  // =============================
  // Change password handler
  // =============================
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (changing) return;
    setErrorMsg("");

    const userId = localStorage.getItem("userId");

    if (!userId) {
      setErrorMsg("User not logged in");
      showToast.error("User not logged in");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setErrorMsg("Please fill in both password fields");
      showToast.error("Please fill in both password fields");
      return;
    }

    if (newPassword.length < 4) {
      setErrorMsg("Password must be at least 4 characters");
      showToast.error("Password must be at least 4 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("New password and confirm password do not match");
      showToast.error("Passwords do not match");
      return;
    }

    setChanging(true);

    try {
      const response = await fetch(`${BASE_API_URL}/profile/change-password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          userId,
          newPassword,
          confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMsg(data.error || "Password update failed");
        showToast.error(data.error || "Password update failed");
        setChanging(false);
        return;
      }

      showToast.save("Password updated successfully");
      setNewPassword("");
      setConfirmPassword("");
      setErrorMsg("");
    } catch (error) {
      console.error(error);
      setErrorMsg("Server connection error");
      showToast.error("Server connection error");
    } finally {
      setChanging(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return date;
    }
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  const userInitial = (profile?.name || "U").charAt(0).toUpperCase();

  return (
    <div className="profile_page_container">
      {/* HEADER & BREADCRUMB */}
      <div className="profile_header_section">
        <div className="profile_header_left">
          <div className="profile_breadcrumb">
            <Link to="/home">
              <i className="fa-solid fa-house"></i> Dashboard
            </Link>
            <i className="fa-solid fa-chevron-right separator"></i>
            <span className="current">My Profile</span>
          </div>
          <h1 className="profile_main_title">My Profile</h1>
          <p className="profile_subtitle">
            Manage your account settings, personal details and security credentials
          </p>
        </div>

        {selectedCompany && (
          <div className="profile_company_badge">
            <i className="fa-solid fa-building"></i>
            <div>
              <span className="badge_label">Active Company</span>
              <span className="badge_company_name">{selectedCompany.name}</span>
            </div>
          </div>
        )}
      </div>

      {/* MAIN GRID LAYOUT */}
      <div className="profile_content_grid">
        {/* LEFT COLUMN: IDENTITY & PASSWORD */}
        <div className="profile_left_column">
          {/* Avatar Card */}
          <div className="profile_user_card">
            <div className="avatar_wrapper">
              <div className="profile_avatar_circle">{userInitial}</div>
              <span className="avatar_online_dot" title="Active Status"></span>
            </div>

            <h2 className="user_card_name">{profile?.name || "User"}</h2>
            <p className="user_card_email">
              <i className="fa-regular fa-envelope"></i> {profile?.email || "No email provided"}
            </p>

            <div className="user_badge_row">
              <span className={`status_pill ${profile?.active !== false ? "active" : "inactive"}`}>
                <span className="status_dot"></span>
                {profile?.active !== false ? "Active Account" : "Inactive"}
              </span>

              <span className="role_pill">
                <i className="fa-solid fa-shield"></i> Standard User
              </span>
            </div>
          </div>

          {/* Change Password Card */}
          <div className="profile_password_card">
            <div className="card_header">
              <div className="card_header_icon">
                <i className="fa-solid fa-key"></i>
              </div>
              <div>
                <h3>Change Password</h3>
                <p>Update your login credentials</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="password_form">
              <div className="form_group">
                <label>New Password</label>
                <div className="input_with_icon">
                  <i className="fa-solid fa-lock input_icon"></i>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setErrorMsg("");
                    }}
                  />
                  <button
                    type="button"
                    className="password_toggle_btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex="-1"
                    title={showNewPassword ? "Click to hide password" : "Click to view password"}
                  >
                    <i className={showNewPassword ? "fa-solid fa-eye" : "fa-solid fa-eye-slash"}></i>
                  </button>
                </div>
              </div>

              <div className="form_group">
                <label>Confirm Password</label>
                <div className="input_with_icon">
                  <i className="fa-solid fa-shield-halved input_icon"></i>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrorMsg("");
                    }}
                  />
                  <button
                    type="button"
                    className="password_toggle_btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                    title={showConfirmPassword ? "Click to hide password" : "Click to view password"}
                  >
                    <i className={showConfirmPassword ? "fa-solid fa-eye" : "fa-solid fa-eye-slash"}></i>
                  </button>
                </div>
              </div>

              {errorMsg && (
                <div className="password_error_alert">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                className="update_password_btn"
                disabled={changing || !newPassword}
              >
                {changing ? (
                  <>
                    <span className="btn_spinner"></span> Updating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-lock-open"></i> Update Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* RIGHT COLUMN: INFORMATION SECTIONS */}
        <div className="profile_right_column">
          {/* Basic Information Section */}
          <div className="profile_info_section">
            <div className="section_title_row">
              <div className="section_title_left">
                <div className="section_icon_box">
                  <i className="fa-regular fa-id-badge"></i>
                </div>
                <div>
                  <h3>Basic Information</h3>
                  <p>Personal details and contact information</p>
                </div>
              </div>
            </div>

            <div className="info_grid">
              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-regular fa-user"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Full Name</span>
                  <span className="info_value">{profile?.name || "N/A"}</span>
                </div>
              </div>

              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-regular fa-envelope"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Email Address</span>
                  <span className="info_value">{profile?.email || "N/A"}</span>
                </div>
              </div>

              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-solid fa-phone"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Mobile Number</span>
                  <span className="info_value">{profile?.mobile || "N/A"}</span>
                </div>
              </div>

              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-solid fa-location-dot"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Address</span>
                  <span className="info_value">{profile?.address || "N/A"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Details Section */}
          <div className="profile_info_section">
            <div className="section_title_row">
              <div className="section_title_left">
                <div className="section_icon_box">
                  <i className="fa-solid fa-sliders"></i>
                </div>
                <div>
                  <h3>Account & Subscription Details</h3>
                  <p>System permissions and registration timeline</p>
                </div>
              </div>
            </div>

            <div className="info_grid">
              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-solid fa-building-user"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Max Companies Allowed</span>
                  <span className="info_value company_limit_badge">
                    {profile?.maxCompanies || 5} Companies
                  </span>
                </div>
              </div>

              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-regular fa-calendar-check"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Account Created</span>
                  <span className="info_value">
                    {formatDate(profile?.accountCreatedOn)}
                  </span>
                </div>
              </div>

              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-solid fa-shield-halved"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">Account Security</span>
                  <span className="info_value text-teal-600 font-semibold">
                    <i className="fa-solid fa-circle-check text-xs mr-1 text-teal-600"></i> Protected
                  </span>
                </div>
              </div>

              <div className="info_card">
                <div className="info_icon">
                  <i className="fa-solid fa-circle-info"></i>
                </div>
                <div className="info_content">
                  <span className="info_label">User ID</span>
                  <span className="info_value">
                    #{localStorage.getItem("userId") || "1"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
